import { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { showSuccessToast, showErrorToast } from '../../utils/notificationUtils';
import { outpatientAPI, inpatientAPI } from '../../services/api';
import '../../pages/reception/ReceptionStyles.css';

const SOAPDetailsContent = ({
  isVisible,
  onClose,
  outpatientId,
  inpatientId,
  onSave,
  finalDiagnosis,
  visitInfo,
  services = [],
  onInitialDiagnosisUpdate,
  visitInitialDiagnosis,
  readOnly = false,
  petId,
  hideHeader = false,
  // Controls for Plan section behavior
  showPlanServices = true,
  planServicesMode = 'all', // 'all' | 'fulfilledOnly'
  allowAutoLoadServices = true,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [soapData, setSoapData] = useState({
    serial: '',
    dateTime: new Date().toISOString().slice(0, 16),
    ownerComplaint: '',
    subjective: '',
    severity: '',
    objective: '',
    initialDiagnosis: '',
    assessment: '',
    plan: '',
    history: [], // Stores previous differential diagnoses
    finalDiagnosis: '' // Added for read-only view
  });

  const [vitalSigns, setVitalSigns] = useState({
    serial: '',
    temperature: '',
    heartRate: '',
    respiratoryRate: '',
    capillaryRefillTime: '',
    bodyWeight: '',
    hydrationStatus: '',
    mucousMembraneColor: '',
    bodyConditionScore: '',
    urine: '',
    stool: '',
    oralCavity: '',
    lymphNodes: ''
  });

  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [errors, setErrors] = useState({});
  const [openSections, setOpenSections] = useState({
    subjective: readOnly,
    objective: readOnly,
    assessment: readOnly,
    plan: readOnly
  });

  // Read-view vs edit-form toggle: starts in edit mode (current behavior);
  // flips to read view when an existing note is loaded.
  const [isEditing, setIsEditing] = useState(true);

  // Day Selector States
  const [selectedDay, setSelectedDay] = useState(1);
  const [days, setDays] = useState([]);
  const [allVitalSigns, setAllVitalSigns] = useState({}); // Map of Day -> Record

  // Determine visit ID and type
  const visitId = inpatientId || outpatientId;
  const visitType = inpatientId ? 'inpatient' : 'outpatient';

  // Use refs to track if we've already fetched to prevent duplicate calls
  const hasFetchedSOAPRef = useRef(false);
  const hasFetchedVitalSignsRef = useRef(false);
  const hasFetchedVisitInfoRef = useRef(false);
  const hasFetchedServicesRef = useRef(false);

  const [internalVisitInfo, setInternalVisitInfo] = useState(null);
  const [internalServices, setInternalServices] = useState([]);
  const effectiveVisitInfo = visitInfo || internalVisitInfo;
  const effectiveServices = (services && services.length > 0) ? services : internalServices;
  const planServices = useMemo(() => {
    if (!effectiveServices || effectiveServices.length === 0) return [];
    if (planServicesMode === 'fulfilledOnly') {
      return effectiveServices.filter(s => !!(s.is_fulfilled ?? s.fulfilled));
    }
    return effectiveServices;
  }, [effectiveServices, planServicesMode]);

  // Reset fetch flags when visitId changes to ensure fresh data (e.g., viewing past visits or different accounts)
  useEffect(() => {
    hasFetchedSOAPRef.current = false;
    hasFetchedVitalSignsRef.current = false;
    hasFetchedVisitInfoRef.current = false;
    hasFetchedServicesRef.current = false;
  }, [visitId]);

  // One-call visit summary (visit + SOAP + vitals). Falls back to the
  // individual endpoints when the summary route is unavailable (older
  // backend) or errors.
  const loadSummary = async () => {
    const runIndividual = () => {
      if (!visitInfo && !hasFetchedVisitInfoRef.current) { loadVisitInfo(); hasFetchedVisitInfoRef.current = true; }
      if (!hasFetchedSOAPRef.current) { loadExistingSOAPData(); hasFetchedSOAPRef.current = true; }
      if (!hasFetchedVitalSignsRef.current) { loadExistingVitalSigns(); hasFetchedVitalSignsRef.current = true; }
      if (allowAutoLoadServices && (!services || services.length === 0) && !hasFetchedServicesRef.current) { loadServices(); hasFetchedServicesRef.current = true; }
    };

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/visit-summary/${visitType}/${visitId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) { runIndividual(); return; }

      const json = await response.json();
      const summary = json.data || {};

      if (!visitInfo && summary.visit) {
        await loadVisitInfo({ status: 'success', data: summary.visit });
        hasFetchedVisitInfoRef.current = true;
      }
      if (Array.isArray(summary.soap_notes)) {
        await loadExistingSOAPData({ status: 'success', data: summary.soap_notes });
        hasFetchedSOAPRef.current = true;
      } else {
        loadExistingSOAPData();
        hasFetchedSOAPRef.current = true;
      }
      if (Array.isArray(summary.vital_signs)) {
        await loadExistingVitalSigns({ status: 'success', data: summary.vital_signs });
        hasFetchedVitalSignsRef.current = true;
      } else {
        loadExistingVitalSigns();
        hasFetchedVitalSignsRef.current = true;
      }
      if (allowAutoLoadServices && (!services || services.length === 0) && summary.visit) {
        await loadServices({ status: 'success', data: summary.visit });
        hasFetchedServicesRef.current = true;
      }
    } catch (error) {
      runIndividual();
    }
  };

  // Load existing SOAP data and vital signs when component becomes visible
  useEffect(() => {
    if (isVisible && visitId) {
      if (!hasFetchedSOAPRef.current) {
        // Marks all sections upfront so the fallback inside loadSummary
        // can't double-fetch; each loader records its own completion.
        hasFetchedSOAPRef.current = true;
        loadSummary();
      }
    } else if (!isVisible) {
      // Reset refs when component closes
      hasFetchedSOAPRef.current = false;
      hasFetchedVitalSignsRef.current = false;
      hasFetchedVisitInfoRef.current = false;
      hasFetchedServicesRef.current = false;
    }
  }, [isVisible, visitId, visitType, allowAutoLoadServices]);

  // Auto-populate Initial Diagnosis from visit's initial diagnosis if it exists and SOAP Initial Diagnosis is empty
  useEffect(() => {
    if (visitInitialDiagnosis && visitInitialDiagnosis.trim() && !soapData.initialDiagnosis.trim() && (!soapData.history || soapData.history.length === 0)) {
      setSoapData(prev => ({
        ...prev,
        initialDiagnosis: visitInitialDiagnosis
      }));
    }
  }, [visitInitialDiagnosis, soapData.initialDiagnosis]);

  const loadExistingSOAPData = async (injectedJson = null) => {
    if (!visitId) return;

    setLoadingExisting(true);
    try {
      let data = injectedJson;
      if (!data) {
        const token = localStorage.getItem('access_token');
        const endpoint = visitType === 'inpatient'
          ? `${import.meta.env.VITE_API_URL}/api/v1/soap-notes/inpatient-visit/${visitId}`
          : `${import.meta.env.VITE_API_URL}/api/v1/soap-notes/outpatient-visit/${visitId}`;

        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message ||
            (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : null) ||
            'Failed to load SOAP notes';
          showErrorToast(errorMessage, {
            title: 'Loading Error',
            duration: 5000
          });
          return;
        }
        data = await response.json();
      }

      {
        if (data.data && data.data.length > 0) {
          // Use the most recent SOAP record
          const latestSOAP = data.data[0];
          setSoapData({
            serial: latestSOAP.serial || '',
            dateTime: latestSOAP.date_time || new Date().toISOString().slice(0, 16),
            ownerComplaint: latestSOAP.owner_complaint || '',
            subjective: latestSOAP.subjective || '',
            severity: latestSOAP.severity || '',
            objective: latestSOAP.objective || '',
            // Use SOAP initial diagnosis if it exists, otherwise fall back to visit's initial diagnosis
            initialDiagnosis: latestSOAP.initial_diagnosis || visitInitialDiagnosis || '',
            assessment: latestSOAP.assessment || '',
            plan: latestSOAP.plan || '',
            history: latestSOAP.diagnosis_history || [],
            finalDiagnosis: latestSOAP.final_diagnosis || ''
          });

          // An existing note was found — show the read view first
          setIsEditing(false);

          // If plan is empty and we have services, we could auto-fill it, 
          // but better to just display them in the render logic to avoid polluting the DB field unnecessarily 
          // unless the user wants to edit it.
        } else {
          // No existing SOAP data, auto-populate initial diagnosis from visit if it exists
          if (visitInitialDiagnosis && visitInitialDiagnosis.trim()) {
            setSoapData(prev => ({
              ...prev,
              initialDiagnosis: visitInitialDiagnosis
            }));
          }
        }
      }
    } catch (error) {
      showErrorToast('An error occurred while loading SOAP notes. Please try again.', {
        title: 'Error',
        duration: 5000
      });
    } finally {
      setLoadingExisting(false);
    }
  };

  const loadVisitInfo = async (injectedRawResponse = null) => {
    if (!visitId) return;
    try {
      const rawResponse = injectedRawResponse ?? (await (visitType === 'inpatient'
        ? inpatientAPI.getInpatientVisit(visitId)
        : outpatientAPI.getOutpatientVisit(visitId))).data;

      const data = rawResponse?.status === 'success' ? rawResponse.data : rawResponse;

      if (data) {
        // Handle cases where data is an array (services only) vs object (visit info + services)
        const visit = (!Array.isArray(data) && (data.visit || data.inpatient_visit || data.outpatient_visit || data))
          ? (data.visit || data.inpatient_visit || data.outpatient_visit || data)
          : null;

        if (visit && typeof visit === 'object') {
          setInternalVisitInfo({
            visitDate: visit.visit_date || visit.created_at || visit.admission_date,
            initialDiagnosis: visit.initial_diag || visit.initial_diagnosis || visit.reason || visit.complaint || '',
            finalDiagnosis: visit.final_diag || visit.final_diagnosis || '',
            doctorNotes: visit.doctor_notes || '',
            prescription: visit.prescription || ''
          });
        }
      }
    } catch (err) {}
  };

  const loadServices = async (injectedRawResponse = null) => {
    if (!visitId) return;
    try {
      // Try the primary endpoint based on visitType
      let rawResponse = injectedRawResponse ?? (await (visitType === 'inpatient'
        ? inpatientAPI.getInpatientVisit(visitId)
        : outpatientAPI.getOutpatientVisit(visitId))).data;
      let data = rawResponse?.status === 'success' ? rawResponse.data : rawResponse;

      // Deep helper to find services array in unknown structures
      const findServices = (obj) => {
        if (!obj) return [];
        if (Array.isArray(obj)) return obj;
        return obj.services || obj.visit_details || obj.visitDetails ||
          obj.inpatient_visit_details || obj.outpatient_visit_details ||
          obj.details || obj.items || [];
      };

      let rawServices = findServices(data);

      // Also check if the services were already provided in the visitInfo prop for fallbacks
      if (rawServices.length === 0 && visitInfo?.services) {
        rawServices = findServices(visitInfo.services);
      }

      // If visit key exists, check inside it too
      if (rawServices.length === 0 && data && typeof data === 'object') {
        const visitObj = data.visit || data.inpatient_visit || data.outpatient_visit;
        if (visitObj) rawServices = findServices(visitObj);
      }

      // No cross-type probing here: an outpatient ID can never exist in the
      // inpatient table (and vice versa) — querying the other type with the
      // same ID only produced a guaranteed 404 in the network log.

      if (rawServices && Array.isArray(rawServices)) {
        const normalized = rawServices.map(s => {
          // Extremely robust name extraction
          const name = s.clinic_service?.service_name_en ||
            s.service?.service_name_en ||
            s.clinic_service?.name ||
            s.service?.name ||
            s.service_name_en ||
            s.service_name ||
            s.name ||
            s.title ||
            `Service #${s.id || '?'}`;

          return {
            ...s,
            id: s.id,
            name: name,
            qty: s.qty || s.quantity || 1,
            fulfilled: s.is_fulfilled !== undefined ? !!s.is_fulfilled : (s.fulfilled !== undefined ? !!s.fulfilled : true),
            notes: s.notes || ''
          };
        });
        setInternalServices(normalized);
      }
    } catch (err) {}
  };

  const handleAddDifferential = () => {
    if (!soapData.initialDiagnosis.trim()) return;

    // Get current user info
    let doctorName = 'Doctor';
    try {
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      if (userData.name) {
        doctorName = `Dr. ${userData.name}`;
      } else if (userData.first_name) {
        doctorName = `Dr. ${userData.first_name} ${userData.last_name || ''}`;
      }
    } catch (e) {}

    const newEntry = {
      diagnosis: soapData.initialDiagnosis,
      date: new Date().toISOString(),
      by: doctorName
    };

    setSoapData(prev => ({
      ...prev,
      history: [...(prev.history || []), newEntry], // Append new entry to history
      initialDiagnosis: '' // Clear current field for new input
    }));
  };

  const loadPreviousSOAPData = async () => {
    if (!petId || soapData.history || soapData.ownerComplaint) return; // Don't overwrite if user already typed or if we have data

    try {
      const token = localStorage.getItem('access_token');
      // Search for previous inpatient visits for this pet
      // We'll use the search-inpatient-visits endpoint to find the last visit

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments/search-inpatient-visits?pet_id=${petId}&sort_by=date&sort_order=desc`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const json = await response.json();
        const visits = json.data?.inpatient_visits || [];

        // Find the most recent visit that IS NOT the current one
        const previousVisit = visits.find(v => String(v.id) !== String(inpatientId));

        if (previousVisit) {
          // Now fetch the SOAP for this previous visit to get history/complaint if available
          // Or if the visit itself has some history fields, we use them.
          // Typically history/complaint is in SOAP.
          const soapResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/soap-notes/inpatient-visit/${previousVisit.id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (soapResponse.ok) {
            const soapJson = await soapResponse.json();
            if (soapJson.data && soapJson.data.length > 0) {
              const lastSoap = soapJson.data[0];

              // Pre-fill history and complaint
              setSoapData(prev => ({
                ...prev,
                ownerComplaint: lastSoap.owner_complaint || prev.ownerComplaint,
                // We could also pre-fill objective or others if requested, but usually history/complaint is what users want carried over for re-admission
              }));

              showSuccessToast('History pre-filled from previous visit', { duration: 3000 });
            }
          }
        }
      }
    } catch (err) {}
  };

  // Trigger pre-fill after loading existing data if no data was found
  useEffect(() => {
    if (!loadingExisting && isVisible && !soapData.serial && petId) {
      // If we finished loading existing and found nothing (no serial), try to pre-fill
      loadPreviousSOAPData();
    }
  }, [loadingExisting, isVisible, soapData.serial, petId]);

  const loadExistingVitalSigns = async (injectedResult = null) => {
    if (!visitId) return;

    try {
      let result = injectedResult;
      if (!result) {
        const endpoint = visitType === 'inpatient'
          ? `${import.meta.env.VITE_API_URL}/api/v1/vital-signs/inpatient-visit/${visitId}`
          : `${import.meta.env.VITE_API_URL}/api/v1/vital-signs/outpatient-visit/${visitId}`;

        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) return;
        result = await response.json();
      }

      {

        // Always calculate days if visitDate is available
        const visitDate = effectiveVisitInfo?.visitDate;
        if (visitDate) {
          const startDate = new Date(visitDate);
          startDate.setHours(0, 0, 0, 0);

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const diffTime = today - startDate;
          const diffDays = Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
          const totalDays = diffDays + 1;
          const daysArray = Array.from({ length: totalDays }, (_, i) => i + 1);
          setDays(daysArray);

          // Set initial selected day if not already set (default to last day)
          // But we don't want to override user selection if they are browsing
          // Only set if we haven't set it yet? Or if it's the first load?
          // Actually, we usually want to default to today/last day on load.
          // Let's check if selectedDay is valid, if not (or if it's 1 and we have more days), maybe update?
          // Safer to just ensure selectedDay is within range after calculation.
          if (daysArray.length > 0 && (!selectedDay || selectedDay > totalDays)) {
            setSelectedDay(daysArray[daysArray.length - 1]);
          }
        }

        if (result.status === 'success' && result.data && Array.isArray(result.data)) {
          // Process all vital signs records into days
          const processed = {};

          if (visitDate) {
            const startDate = new Date(visitDate);
            startDate.setHours(0, 0, 0, 0);

            result.data.forEach(record => {
              // Backend now returns `date` (Y-m-d). Prefer it over created_at for day calculation.
              const recordDateStr = record.date || record.vital_sign_date || record.created_at;
              const recordDate = new Date(recordDateStr);
              recordDate.setHours(0, 0, 0, 0);

              const diffTime = recordDate - startDate;
              const dayOffset = Math.floor(diffTime / (1000 * 60 * 60 * 24));
              const dayNumber = dayOffset + 1;

              if (dayNumber > 0) {
                processed[dayNumber] = {
                  id: record.id,
                  serial: record.serial || '',
                  temperature: record.temperature ? record.temperature.replace('°C', '') : '',
                  heartRate: record.heart_rate ? record.heart_rate.replace(' bpm', '') : '',
                  respiratoryRate: record.respiratory_rate ? record.respiratory_rate.replace(' breaths/min', '') : '',
                  capillaryRefillTime: record.capillary_refill_time || '',
                  bodyWeight: record.body_weight || '',
                  hydrationStatus: record.hydration_status || '',
                  mucousMembraneColor: record.mucous_membrane_color || '',
                  bodyConditionScore: record.body_condition_score || '',
                  urine: record.urine || '',
                  stool: record.stool || '',
                  oralCavity: record.oral_cavity || record.oralCavity || '',
                  lymphNodes: record.lymph_nodes || '',
                  severity: record.severity || '',
                  objective: record.objective || record.physical_exam_findings || '',
                  date: record.date || recordDateStr // keep date for reference/debug
                };
              }
            });

            setAllVitalSigns(processed);

            // Load data for the currently selected day (or the one we just defaulted to)
            // We need to know which day is effectively selected now.
            // Since state updates are async, we use the logic:
            // const effectivelySelectedDay = (days.length > 0 && (!selectedDay || selectedDay > days.length)) 
            //       ? days[days.length - 1] 
            //       : (selectedDay || 1);

            // Wait, 'days' state from above setDays won't be available immediately here?
            // YES, good catch. 'days' above refers to the State which isn't updated yet.
            // We should use the calculated 'daysArray'.
            // We also need to recalculate totalDays here to be safe if we rely on it.
            // Re-use logic:
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const startDateForCalc = new Date(visitDate);
            startDateForCalc.setHours(0, 0, 0, 0);
            const diffTimeCalc = today - startDateForCalc;
            const diffDaysCalc = Math.max(0, Math.floor(diffTimeCalc / (1000 * 60 * 60 * 24)));
            const totalDaysCalc = diffDaysCalc + 1;

            const currentDayToLoad = totalDaysCalc; // Default to latest day on load

            // If we are just loading, we probably want to show the latest data?
            // Or preserve selectedDay if we are re-fetching?
            // Let's assume we load the LAST available day (TODAY) initially.
            if (!hasFetchedVitalSignsRef.current) { // Only on initial fetch
              setSelectedDay(currentDayToLoad);
              const dayData = processed[currentDayToLoad] || {};
              setVitalSigns(prev => ({ ...prev, ...dayData }));
              if (dayData.severity) setSoapData(prev => ({ ...prev, severity: dayData.severity }));
              if (dayData.objective) setSoapData(prev => ({ ...prev, objective: dayData.objective }));
            } else {
              // If re-fetching (e.g. after save), refresh the currently selected day
              const dayData = processed[selectedDay] || {};
              setVitalSigns(prev => ({ ...prev, ...dayData }));
              // Also sync severity and objective to soapData
              if (dayData.severity) setSoapData(prev => ({ ...prev, severity: dayData.severity }));
              if (dayData.objective) setSoapData(prev => ({ ...prev, objective: dayData.objective }));
            }
          }
        }
      }
    } catch (error) {
      // Don't show toast for vital signs loading errors as they're not critical
    }
  };

  const handleInputChange = (field, value) => {
    setSoapData(prev => ({
      ...prev,
      [field]: value
    }));

    // If updating fields that are also in vital signs (severity, objective),
    // update allVitalSigns map too so it persists when switching days
    if (field === 'severity' || field === 'objective') {
      setAllVitalSigns(prev => ({
        ...prev,
        [selectedDay]: {
          ...(prev[selectedDay] || {}),
          [field]: value
        }
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleVitalSignsChange = (field, value) => {
    setVitalSigns(prev => {
      const newData = { ...prev, [field]: value };
      // Optimistically update the allVitalSigns map too
      setAllVitalSigns(all => ({
        ...all,
        [selectedDay]: {
          ...(all[selectedDay] || {}),
          [field]: value
        }
      }));
      return newData;
    });
  };

  // Handle Day Switch
  const handleDaySelect = (day) => {
    setSelectedDay(day);
    const dayData = allVitalSigns[day] || {};

    // Update Vital Signs Form
    setVitalSigns(() => ({
      serial: dayData.serial || '',
      temperature: dayData.temperature || '',
      heartRate: dayData.heartRate || '',
      respiratoryRate: dayData.respiratoryRate || '',
      capillaryRefillTime: dayData.capillaryRefillTime || '',
      bodyWeight: dayData.bodyWeight || '',
      hydrationStatus: dayData.hydrationStatus || '',
      mucousMembraneColor: dayData.mucousMembraneColor || '',
      bodyConditionScore: dayData.bodyConditionScore || '',
      urine: dayData.urine || '',
      stool: dayData.stool || '',
      oralCavity: dayData.oralCavity || '',
      lymphNodes: dayData.lymphNodes || ''
    }));

    // Update Linked Fields (Severity, Objective)
    setSoapData(prev => ({
      ...prev,
      severity: dayData.severity || '',
      objective: dayData.objective || ''
    }));
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const missingFields = [];

    if (!soapData.ownerComplaint.trim()) {
      newErrors.ownerComplaint = t('receptionOutpatients.details.soapNotes.errors.ownerComplaint', { defaultValue: "Guardian's complaint is required" });
      missingFields.push('Guardian Complaint');
    }

    if (!soapData.severity) {
      newErrors.severity = t('receptionOutpatients.details.soapNotes.errors.painScore', { defaultValue: 'Pain score is required' });
      missingFields.push('Pain score');
    }

    if (!soapData.objective.trim()) {
      newErrors.objective = t('receptionOutpatients.details.soapNotes.errors.objective', { defaultValue: 'Objective findings are required' });
      missingFields.push('Physical Exam Findings');
    }

    if (!soapData.initialDiagnosis.trim()) {
      newErrors.initialDiagnosis = t('receptionOutpatients.details.soapNotes.errors.initialDiagnosis', { defaultValue: 'Initial diagnosis is required' });
      missingFields.push('Initial Diagnosis');
    }

    setErrors(newErrors);

    // Show toast message if there are validation errors
    if (Object.keys(newErrors).length > 0) {
      const errorMessage = missingFields.length === 1
        ? `Please fill in the required field: ${missingFields[0]}`
        : `Please fill in the required fields: ${missingFields.join(', ')}`;
      showErrorToast(errorMessage, {
        title: 'Validation Error',
        duration: 5000
      });
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');

      // Prepare payload with appropriate visit ID
      const payload = {
        serial: soapData.serial,
        date_time: soapData.dateTime,
        owner_complaint: soapData.ownerComplaint,
        objective: soapData.objective,
        severity: soapData.severity,
        initial_diagnosis: soapData.initialDiagnosis,
        assessment: soapData.assessment,
        plan: soapData.plan,
        diagnosis_history: soapData.history,
        final_diagnosis: (soapData.initialDiagnosis && soapData.initialDiagnosis.trim())
          ? soapData.initialDiagnosis.trim()
          : (soapData.history && soapData.history.length > 0 ? soapData.history[soapData.history.length - 1].diagnosis : soapData.initialDiagnosis)
      };

      // Add the appropriate visit ID based on visit type
      if (visitType === 'inpatient') {
        payload.inpatient_visit_id = parseInt(inpatientId);
      } else {
        payload.outpatient_visit_id = parseInt(outpatientId);
      }


      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/soap-notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // Also save vital signs if any fields are filled
        const hasVitalSigns = Object.values(vitalSigns).some(val => val && String(val).trim() !== '');
        if (hasVitalSigns) {
          await saveVitalSigns();
        }

        // Sync the diagnosis to the visit record itself so the Summary tab
        // (which reads visit.final_diag / initial_diag) reflects the note.
        try {
          const diagPayload = {};
          // Subjective ("Guardian's complaint") doubles as the visit's main
          // complaint. The S textarea binds ownerComplaint, so it takes
          // precedence over the loaded subjective value.
          const complaint = (soapData.ownerComplaint || soapData.subjective || '').trim();
          if (complaint) {
            diagPayload.main_complaint = complaint;
          }
          // Initial impression = the FIRST diagnosis ever recorded; the
          // current textarea may hold a newer differential, so prefer the
          // first history entry (fall back to the field when no history).
          const firstDiag = ((soapData.history && soapData.history[0] && soapData.history[0].diagnosis) || soapData.initialDiagnosis || '').trim();
          if (firstDiag) {
            diagPayload.initial_diag = firstDiag;
          }
          const finalDiag = String(payload.final_diagnosis || '').trim();
          if (finalDiag) {
            diagPayload.final_diag = finalDiag;
          }
          if (Object.keys(diagPayload).length > 0) {
            const visitUrl = visitType === 'inpatient'
              ? `${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/${inpatientId}`
              : `${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/${outpatientId}`;
            await fetch(visitUrl, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
              },
              body: JSON.stringify(diagPayload),
            });
          }
        } catch (syncError) {
          // Note is already saved; the visit sync is best-effort
          console.warn('Could not sync diagnosis to the visit record:', syncError);
        }

        // Update visit's initial diagnosis if provided
        if (soapData.initialDiagnosis && onInitialDiagnosisUpdate) {
          try {
            await onInitialDiagnosisUpdate(soapData.initialDiagnosis);
          } catch (error) {
            // Don't show error toast as SOAP was saved successfully
          }
        }

        // Call parent's onSave callback (which will show toast and refresh)
        setIsEditing(false);
        onSave && onSave();
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message ||
          (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : null) ||
          'Failed to save SOAP notes';
        showErrorToast(errorMessage, {
          title: 'Save Error',
          duration: 5000
        });
      }
    } catch (error) {
      const errorMessage = error.message || 'An error occurred while saving SOAP notes. Please try again.';
      showErrorToast(errorMessage, {
        title: 'Error',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const saveVitalSigns = async () => {
    try {
      const promises = [];
      const token = localStorage.getItem('access_token');

      // Iterate through all days in allVitalSigns and save them
      // This ensures edits on Day 1 are saved even if we are currently looking at Day 2
      const daysToSave = Object.keys(allVitalSigns).length > 0
        ? Object.keys(allVitalSigns)
        : [selectedDay]; // Fallback if map is empty (shouldn't happen if edits occurred)

      for (const dayStr of daysToSave) {
        const day = parseInt(dayStr);
        const dayData = allVitalSigns[day] || vitalSigns; // Fallback to current state if map missing logic

        // Skip empty records if they don't have an ID (don't create empty spam)
        const hasContent = dayData.temperature || dayData.heartRate || dayData.respiratoryRate ||
          dayData.objective || dayData.severity || dayData.notes;
        if (!dayData.id && !hasContent) continue;

        // Prepare payload from the day's data (NOT just current state)
        const payload = {
          serial: dayData.serial || '',
          temperature: dayData.temperature ? `${parseFloat(dayData.temperature)}°C` : '',
          heart_rate: dayData.heartRate ? `${parseInt(dayData.heartRate)} bpm` : '',
          respiratory_rate: dayData.respiratoryRate ? `${parseInt(dayData.respiratoryRate)} breaths/min` : '',
          capillary_refill_time: dayData.capillaryRefillTime || '',
          body_weight: parseFloat(dayData.bodyWeight) || 0,
          mucous_membrane_color: dayData.mucousMembraneColor || '',
          body_condition_score: dayData.bodyConditionScore || '',
          hydration_status: dayData.hydrationStatus || '',
          urine: dayData.urine || '',
          stool: dayData.stool || '',
          oral_cavity: dayData.oralCavity || '',
          lymph_nodes: dayData.lymphNodes || '',
          severity: dayData.severity || '', // Use data from map
          objective: dayData.objective || '' // Use data from map
        };

        if (visitType === 'inpatient') {
          payload.inpatient_visit_id = parseInt(inpatientId);
        } else {
          payload.outpatient_visit_id = parseInt(outpatientId);
        }

        // Calculate Date for this specific day
        if (visitInfo && visitInfo.visitDate) {
          const targetDate = new Date(visitInfo.visitDate);
          targetDate.setDate(targetDate.getDate() + (day - 1));
          const dateStr = targetDate.toISOString().slice(0, 19).replace('T', ' ');
          payload.date = dateStr;
        }

        let url = `${import.meta.env.VITE_API_URL}/api/v1/vital-signs`;
        let method = 'POST';

        if (dayData.id) {
          url = `${url}/${dayData.id}`;
          method = 'PUT';
          payload.id = dayData.id;
        }

        promises.push(
          fetch(url, {
            method: method,
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(payload)
          }).then(async res => {
            if (res.ok) {
              const json = await res.json();
              // Update ID in state if created
              if (method === 'POST' && json.data?.id) {
                setAllVitalSigns(prev => ({
                  ...prev,
                  [day]: {
                    ...prev[day],
                    id: json.data.id
                  }
                }));
              }
              return { success: true, day };
            } else {
              const err = await res.json().catch(() => ({}));
              return { success: false, day, error: err };
            }
          })
        );
      }

      const results = await Promise.all(promises);
      const failures = results.filter(r => !r.success);

      if (failures.length > 0) {
        showErrorToast('Some vital signs data failed to save.', {
          title: 'Partial Save Error',
          duration: 5000
        });
      }

    } catch (error) {
      showErrorToast(error.message || 'An error occurred while saving vital signs. Please try again.', {
        title: 'Error',
        duration: 5000
      });
    }
  };

  const handleCancel = () => {
    // Reset form to original state
    setSoapData({
      serial: '',
      dateTime: new Date().toISOString().slice(0, 16),
      ownerComplaint: '',
      subjective: '',
      severity: '',
      objective: '',
      initialDiagnosis: '',
      assessment: '',
      plan: ''
    });
    setVitalSigns({
      serial: '',
      temperature: '',
      heartRate: '',
      respiratoryRate: '',
      capillaryRefillTime: '',
      bodyWeight: '',
      hydrationStatus: '',
      mucousMembraneColor: '',
      bodyConditionScore: '',
      lymphNodes: ''
    });
    setErrors({});
    setOpenSections({
      subjective: false,
      objective: false,
      assessment: false,
      plan: false
    });
    onClose();
  };

  const handleFormCancel = () => {
    // Reload the last saved note into the form; loadExistingSOAPData flips back
    // to the read view when a note exists. When none exists the cleared form
    // simply stays in edit mode.
    setErrors({});
    loadExistingSOAPData();
  };

  // Numbers-only filter: digits + a single decimal point
  const decimalFilter = (value) => {
    const cleaned = String(value).replace(/[^\d.]/g, '');
    const dot = cleaned.indexOf('.');
    if (dot === -1) return cleaned;
    return cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '');
  };

  // Compact vitals chips for the read view (same data the form saves)
  const buildVitalsChips = () => {
    const chips = [];
    if (vitalSigns.temperature) chips.push(`${vitalSigns.temperature}°C`);
    if (vitalSigns.heartRate) chips.push(`${t('receptionOutpatients.details.soapNotes.readView.hr', { defaultValue: 'HR' })} ${vitalSigns.heartRate}`);
    if (vitalSigns.respiratoryRate) chips.push(`${t('receptionOutpatients.details.soapNotes.readView.rr', { defaultValue: 'RR' })} ${vitalSigns.respiratoryRate}`);
    const weight = parseFloat(vitalSigns.bodyWeight);
    if (!Number.isNaN(weight) && weight > 0) {
      chips.push(weight < 1 ? `${Math.round(weight * 1000)} g` : `${weight} kg`);
    }
    if (vitalSigns.hydrationStatus) {
      chips.push(`${t('receptionOutpatients.details.soapNotes.readView.hydration', { defaultValue: 'Hydration' })}: ${vitalSigns.hydrationStatus}`);
    }
    if (vitalSigns.mucousMembraneColor) {
      chips.push(`${t('receptionOutpatients.details.soapNotes.readView.mm', { defaultValue: 'MM' })}: ${vitalSigns.mucousMembraneColor}`);
    }
    if (vitalSigns.severity !== undefined && vitalSigns.severity !== null && vitalSigns.severity !== '') {
      chips.push(`${t('receptionOutpatients.details.soapNotes.readView.pain', { defaultValue: 'Pain' })} ${vitalSigns.severity}/4`);
    }
    return chips;
  };

  if (!isVisible) return null;

  // Calculate Display Final Diagnosis for read-only view
  let displayFinalDiagnosis = finalDiagnosis || soapData.finalDiagnosis;
  const currentInput = soapData.initialDiagnosis ? soapData.initialDiagnosis.trim() : '';

  if (!displayFinalDiagnosis) {
    if (currentInput) {
      displayFinalDiagnosis = currentInput;
    } else if (soapData.history && soapData.history.length > 0) {
      displayFinalDiagnosis = soapData.history[soapData.history.length - 1].diagnosis; // Latest history is at the end
    } else {
      displayFinalDiagnosis = soapData.initialDiagnosis;
    }
  }

  // In read-only mode, fall back to the visit's checkout final diagnosis so the
  // Assessment section already shows the definitive diagnosis.
  if (readOnly && !displayFinalDiagnosis && effectiveVisitInfo?.finalDiagnosis) {
    displayFinalDiagnosis = effectiveVisitInfo.finalDiagnosis;
  }

  // Format services for display in Plan section
  /*
    const formattedServices = (services || [])
      .map(s => {
        const name = s.service_name_en || s.name || 'Service';
        const qty = s.quantity || s.qty || 1;
        const isFulfilled = s.is_fulfilled || s.fulfilled;
        const statusIcon = isFulfilled ? '✅' : '⏳';
        return `${statusIcon} ${name} (x${qty})`;
      }).join('\n');
  */

  return (
    <div className={`soap-details-content-view ${hideHeader ? 'soap-embedded' : ''}`} dir={isRTL ? 'rtl' : 'ltr'} style={{ textAlign: isRTL ? 'right' : 'left' }}>
      {/* Modern Header */}
      {!loadingExisting && !hideHeader && (
        <div className="soap-modern-header">
          <div>
            <h2 className="soap-modern-title">{t('receptionOutpatients.details.soapNotes.title', { defaultValue: 'SOAP Notes' })}</h2>
            {visitInfo && (
              <div className="soap-modern-info">
                {visitInfo.serial && (
                  <span className="soap-modern-info-badge">#{visitInfo.serial}</span>
                )}
                {visitInfo.petName && (
                  <span className="soap-modern-info-badge">{t('receptionOutpatients.details.soapNotes.pet', { defaultValue: 'Pet' })}: {visitInfo.petName}</span>
                )}
                {visitInfo.ownerName && (
                  <span className="soap-modern-info-badge">{t('receptionOutpatients.details.soapNotes.guardian', { defaultValue: 'Guardian' })}: {visitInfo.ownerName}</span>
                )}
              </div>
            )}
          </div>
          <div>
            <button
              onClick={handleCancel}
              disabled={loading}
              className="action-btn cancel-btn"
            >
              {t('common.close', { defaultValue: 'Close' })}
            </button>
          </div>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="overlay-scrollable-content">
        {/* Loading State */}
        {loadingExisting && (
          <div className="overlay-loading">
            <div className="loading-spinner"></div>
            <p>{t('receptionOutpatients.details.soapNotes.loading', { defaultValue: 'Loading SOAP notes...' })}</p>
          </div>
        )}

        {/* Content */}
        {!loadingExisting && (
          isEditing && !readOnly ? (

          <div className="soap-doc-form">
            {/* S — Subjective */}
            <section className="soap-doc-section">
              <div className="soap-doc-head">
                <span className="soap-doc-badge subjective">S</span>
                <div className="soap-doc-head-text">
                  <label className="soap-doc-label">
                    {t('receptionOutpatients.details.soapNotes.sections.subjective.title', { defaultValue: 'Subjective' })}
                  </label>
                  <span className="soap-doc-hint">
                    {t('receptionOutpatients.details.soapNotes.sections.subjective.description', { defaultValue: "Guardian's complaint & pet's history" })}
                  </span>
                </div>
              </div>
              <textarea
                value={soapData.ownerComplaint}
                onChange={(e) => handleInputChange('ownerComplaint', e.target.value)}
                placeholder={t('receptionOutpatients.details.soapNotes.sections.subjective.placeholder', { defaultValue: "Describe the guardian's complaint and the pet's medical history..." })}
                rows={4}
                className={`soap-doc-textarea ${errors.ownerComplaint ? 'error' : ''}`}
              />
              {errors.ownerComplaint && <p className="soap-doc-error">{errors.ownerComplaint}</p>}
            </section>

            {/* O — Objective */}
            <section className="soap-doc-section">
              <div className="soap-doc-head">
                <span className="soap-doc-badge objective">O</span>
                <div className="soap-doc-head-text">
                  <label className="soap-doc-label">
                    {t('receptionOutpatients.details.soapNotes.sections.objective.title', { defaultValue: 'Objective' })}
                  </label>
                  <span className="soap-doc-hint">
                    {t('receptionOutpatients.details.soapNotes.sections.objective.description', { defaultValue: 'Vital signs & physical examination findings' })}
                  </span>
                </div>
              </div>

              {/* Inpatient per-day vitals selector (logic unchanged) */}
              {visitType === 'inpatient' && days.length > 0 && (
                <div className="soap-doc-days">
                  {days.map(day => (
                    <button
                      key={day}
                      type="button"
                      className={`soap-doc-day ${selectedDay === day ? 'active' : ''}`}
                      onClick={() => handleDaySelect(day)}
                    >
                      {t('receptionInpatients.details.dailyCare.day', { defaultValue: 'Day {{count}}', count: day })}
                    </button>
                  ))}
                </div>
              )}

              {/* Primary vitals — compact number inputs with inline units */}
              <div className="soap-doc-vitals-row">
                {[
                  { key: 'temperature', label: t('receptionOutpatients.details.soapForm.temperature', { defaultValue: 'Temp' }), unit: '°C', decimal: true },
                  { key: 'heartRate', label: t('receptionOutpatients.details.soapForm.heartRate', { defaultValue: 'HR' }), unit: 'bpm', decimal: false },
                  { key: 'respiratoryRate', label: t('receptionOutpatients.details.soapForm.respiratoryRate', { defaultValue: 'RR' }), unit: 'rpm', decimal: false },
                  { key: 'bodyWeight', label: t('receptionOutpatients.details.soapForm.weight', { defaultValue: 'Weight' }), unit: 'kg', decimal: true },
                ].map((field) => (
                  <div key={field.key} className="soap-doc-vital">
                    <label>{field.label}</label>
                    <div className="soap-doc-vital-input">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={vitalSigns[field.key]}
                        placeholder="—"
                        onChange={(e) => handleVitalSignsChange(field.key, field.decimal
                          ? decimalFilter(e.target.value)
                          : e.target.value.replace(/[^\d]/g, ''))}
                      />
                      <span>{field.unit}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Secondary vitals — same state fields / payload as before */}
              <div className="soap-doc-vitals-row soap-doc-vitals-secondary">
                {[
                  { key: 'capillaryRefillTime', label: t('receptionOutpatients.details.soapForm.crt', { defaultValue: 'CRT' }) },
                  { key: 'bodyConditionScore', label: t('receptionOutpatients.details.soapForm.bcs', { defaultValue: 'BCS' }) },
                  { key: 'urine', label: t('receptionOutpatients.details.soapForm.urine', { defaultValue: 'Urine' }) },
                  { key: 'stool', label: t('receptionOutpatients.details.soapForm.stool', { defaultValue: 'Stool' }) },
                  { key: 'oralCavity', label: t('receptionOutpatients.details.soapForm.oralCavity', { defaultValue: 'Oral cavity' }) },
                  { key: 'lymphNodes', label: t('receptionOutpatients.details.soapForm.lymphNodes', { defaultValue: 'Lymph nodes' }) },
                ].map((field) => (
                  <div key={field.key} className="soap-doc-vital soap-doc-vital-sm">
                    <label>{field.label}</label>
                    <div className="soap-doc-vital-input">
                      <input
                        type="text"
                        value={vitalSigns[field.key]}
                        placeholder="—"
                        onChange={(e) => handleVitalSignsChange(field.key, e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Hydration chip select */}
              <div className="soap-doc-chip-group">
                <label className="soap-doc-chip-label">
                  {t('receptionOutpatients.details.soapForm.hydration.label', { defaultValue: 'Hydration' })}
                </label>
                <div className="soap-doc-chips">
                  {[
                    { value: 'Normal', label: t('receptionOutpatients.details.soapForm.hydration.normal', { defaultValue: 'Normal' }) },
                    { value: 'Mild 5%', label: t('receptionOutpatients.details.soapForm.hydration.mild', { defaultValue: 'Mild 5%' }) },
                    { value: 'Moderate 8%', label: t('receptionOutpatients.details.soapForm.hydration.moderate', { defaultValue: 'Moderate 8%' }) },
                    { value: 'Severe >10%', label: t('receptionOutpatients.details.soapForm.hydration.severe', { defaultValue: 'Severe >10%' }) },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`soap-doc-chip ${vitalSigns.hydrationStatus === opt.value ? 'on' : ''}`}
                      onClick={() => handleVitalSignsChange('hydrationStatus', vitalSigns.hydrationStatus === opt.value ? '' : opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mucous membranes chip select */}
              <div className="soap-doc-chip-group">
                <label className="soap-doc-chip-label">
                  {t('receptionOutpatients.details.soapForm.mm.label', { defaultValue: 'Mucous membranes' })}
                </label>
                <div className="soap-doc-chips">
                  {[
                    { value: 'Pink', label: t('receptionOutpatients.details.soapForm.mm.pink', { defaultValue: 'Pink' }) },
                    { value: 'Pale', label: t('receptionOutpatients.details.soapForm.mm.pale', { defaultValue: 'Pale' }) },
                    { value: 'Tacky', label: t('receptionOutpatients.details.soapForm.mm.tacky', { defaultValue: 'Tacky' }) },
                    { value: 'Dry', label: t('receptionOutpatients.details.soapForm.mm.dry', { defaultValue: 'Dry' }) },
                    { value: 'Cyanotic', label: t('receptionOutpatients.details.soapForm.mm.cyanotic', { defaultValue: 'Cyanotic' }) },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`soap-doc-chip ${vitalSigns.mucousMembraneColor === opt.value ? 'on' : ''}`}
                      onClick={() => handleVitalSignsChange('mucousMembraneColor', vitalSigns.mucousMembraneColor === opt.value ? '' : opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pain score 0–4 (backend-validated max) */}
              <div className="soap-doc-chip-group">
                <label className="soap-doc-chip-label">
                  {t('receptionOutpatients.details.soapForm.pain', { defaultValue: 'Pain score' })}
                  <span className="soap-doc-hint-inline">
                    {t('receptionOutpatients.details.soapForm.painHint', { defaultValue: '— 0 none · 4 worst' })}
                  </span>
                </label>
                <div className="soap-doc-chips soap-doc-pain">
                  {Array.from({ length: 5 }, (_, n) => (
                    <button
                      key={n}
                      type="button"
                      className={`soap-doc-chip soap-doc-pain-btn ${String(soapData.severity) === String(n) ? 'on' : ''}`}
                      onClick={() => handleInputChange('severity', String(soapData.severity) === String(n) ? '' : String(n))}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              {errors.severity && <p className="soap-doc-error">{errors.severity}</p>}

              <textarea
                value={soapData.objective}
                onChange={(e) => handleInputChange('objective', e.target.value)}
                placeholder={t('receptionOutpatients.details.soapNotes.sections.objective.placeholder', { defaultValue: 'Enter physical examination findings...' })}
                rows={4}
                className={`soap-doc-textarea ${errors.objective ? 'error' : ''}`}
              />
              {errors.objective && <p className="soap-doc-error">{errors.objective}</p>}
            </section>

            {/* A — Assessment */}
            <section className="soap-doc-section">
              <div className="soap-doc-head">
                <span className="soap-doc-badge assessment">A</span>
                <div className="soap-doc-head-text">
                  <label className="soap-doc-label">
                    {t('receptionOutpatients.details.soapNotes.sections.assessment.title', { defaultValue: 'Assessment' })}
                  </label>
                  <span className="soap-doc-hint">
                    {t('receptionOutpatients.details.soapNotes.sections.assessment.description', { defaultValue: 'Diagnosis & clinical assessment' })}
                  </span>
                </div>
              </div>
              <div className="soap-doc-inline-head">
                <span className="soap-doc-req-label">
                  {soapData.history && soapData.history.length > 0
                    ? t('receptionOutpatients.details.soapNotes.sections.assessment.differentialDiagnosis', { defaultValue: 'Differential Diagnosis' })
                    : t('receptionOutpatients.details.soapNotes.sections.assessment.initialDiagnosis', { defaultValue: 'Initial Diagnosis' })}
                  <span className="required">*</span>
                </span>
                <button type="button" className="soap-doc-mini-btn" onClick={handleAddDifferential}>
                  + {t('receptionOutpatients.details.soapForm.addDifferential', { defaultValue: 'Add Differential' })}
                </button>
              </div>
              <textarea
                value={soapData.initialDiagnosis}
                onChange={(e) => handleInputChange('initialDiagnosis', e.target.value)}
                placeholder={t('receptionOutpatients.details.soapNotes.sections.assessment.placeholder', { defaultValue: 'Enter your clinical assessment and diagnosis...' })}
                rows={4}
                className={`soap-doc-textarea ${errors.initialDiagnosis ? 'error' : ''}`}
              />
              {errors.initialDiagnosis && <p className="soap-doc-error">{errors.initialDiagnosis}</p>}

              {/* Diagnosis history — flattened read-only rows */}
              {soapData.history && soapData.history.length > 0 && (
                <div className="soap-doc-history">
                  {[...soapData.history].reverse().map((entry, index) => {
                    const originalIndex = soapData.history.length - 1 - index;
                    return (
                      <div key={index} className="soap-doc-history-row">
                        <span className="soap-doc-history-meta">
                          {originalIndex === 0
                            ? t('receptionOutpatients.details.soapNotes.sections.assessment.initialDiagnosis', { defaultValue: 'Initial Diagnosis' })
                            : `${t('receptionOutpatients.details.soapNotes.sections.assessment.differentialDiagnosis', { defaultValue: 'Differential Diagnosis' })} ${originalIndex}`}
                          {' · '}{entry.by} · {new Date(entry.date).toLocaleString()}
                        </span>
                        <p>{entry.diagnosis}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="soap-doc-final-diag">
                <span className="soap-doc-chip-label">
                  {t('receptionOutpatients.details.soapNotes.sections.assessment.finalDiagnosis', { defaultValue: 'Final Diagnosis' })}
                </span>
                <p>
                  {displayFinalDiagnosis || (
                    <span className="soap-doc-muted">
                      {t('receptionOutpatients.details.soapNotes.sections.assessment.availableAfterCheckout', { defaultValue: 'Final diagnosis will be available after checkout' })}
                    </span>
                  )}
                </p>
              </div>
            </section>

            {/* Checkout summary — flattened inline block */}
            {(effectiveVisitInfo?.doctorNotes || effectiveVisitInfo?.prescription || effectiveVisitInfo?.finalDiagnosis) && (
              <div className="soap-doc-checkout-summary">
                <span className="soap-doc-chip-label">
                  {t('receptionOutpatients.details.soapNotes.sections.summary.title', { defaultValue: 'Summary' })}
                  <span className="soap-doc-hint-inline">
                    {t('receptionOutpatients.details.soapNotes.sections.summary.description', { defaultValue: 'Checkout Summary' })}
                  </span>
                </span>
                {effectiveVisitInfo?.doctorNotes && (
                  <div className="soap-doc-summary-row">
                    <span>{t('receptionOutpatients.details.soapNotes.sections.summary.doctorNotes', { defaultValue: 'Doctor Notes' })}</span>
                    <p>{effectiveVisitInfo.doctorNotes}</p>
                  </div>
                )}
                {effectiveVisitInfo?.prescription && (
                  <div className="soap-doc-summary-row">
                    <span>{t('receptionOutpatients.details.soapNotes.sections.summary.prescription', { defaultValue: 'Prescription' })}</span>
                    <p>{effectiveVisitInfo.prescription}</p>
                  </div>
                )}
                {effectiveVisitInfo?.finalDiagnosis && (
                  <div className="soap-doc-summary-row">
                    <span>{t('receptionOutpatients.details.soapNotes.sections.assessment.finalDiagnosisCheckout', { defaultValue: 'Final Diagnosis (Checkout)' })}</span>
                    <p>{effectiveVisitInfo.finalDiagnosis}</p>
                  </div>
                )}
              </div>
            )}

            {/* P — Plan */}
            <section className="soap-doc-section">
              <div className="soap-doc-head">
                <span className="soap-doc-badge plan">P</span>
                <div className="soap-doc-head-text">
                  <label className="soap-doc-label">
                    {t('receptionOutpatients.details.soapNotes.sections.plan.title', { defaultValue: 'Plan' })}
                  </label>
                  <span className="soap-doc-hint">
                    {t('receptionOutpatients.details.soapNotes.sections.plan.description', { defaultValue: 'Treatment plan & services' })}
                  </span>
                </div>
              </div>
              {showPlanServices && planServices && planServices.length > 0 && (
                <div className="soap-doc-services">
                  {planServices.map((service, index) => {
                    const isFulfilled = service.is_fulfilled || service.fulfilled;
                    return (
                      <div key={service.id || index} className="soap-doc-service-row">
                        <i className={`fas ${isFulfilled ? 'fa-check-circle' : 'fa-circle'}`}></i>
                        <span className="soap-doc-service-name">
                          {service.name || service.service_name_en || `Service #${service.id || index + 1}`}
                        </span>
                        {(service.quantity || service.qty) && (
                          <span className="soap-doc-service-qty">
                            ×{service.quantity || service.qty}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <textarea
                value={soapData.plan}
                onChange={(e) => handleInputChange('plan', e.target.value)}
                placeholder={t('receptionOutpatients.details.soapForm.planPlaceholder', { defaultValue: 'Treatment, follow-up, recheck…' })}
                rows={5}
                className="soap-doc-textarea soap-doc-textarea-plan"
              />
            </section>

            {/* Single action bar */}
            <div className="soap-doc-actions">
              <button
                type="button"
                className="soap-doc-btn ghost"
                onClick={handleFormCancel}
                disabled={loading}
              >
                {t('receptionOutpatients.details.soapForm.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                type="button"
                className="soap-doc-btn primary"
                onClick={handleSave}
                disabled={loading || loadingExisting}
              >
                {loading ? (
                  <>
                    <div className="loading-spinner-small"></div>
                    {t('common.saving', { defaultValue: 'Saving...' })}
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    {t('receptionOutpatients.details.soapForm.save', { defaultValue: 'Save note' })}
                  </>
                )}
              </button>
            </div>
          </div>
          ) : (
            /* ===== Read view: latest note, compact S/O/A/P cards ===== */
            <div className="soap-read-view">
              {!readOnly && (
                <div className="soap-read-actions">
                  <button type="button" className="soap-read-btn" onClick={() => setIsEditing(true)}>
                    <i className="fas fa-pencil-alt"></i>
                    {t('receptionOutpatients.details.soapNotes.readView.editNote', { defaultValue: 'Edit note' })}
                  </button>
                </div>
              )}
              <div className="soap-read-grid">
                <div className="soap-read-card">
                  <div className="soap-read-head">
                    <span className="soap-read-badge subjective">S</span>
                    <h4>{t('receptionOutpatients.details.soapNotes.sections.subjective.title', { defaultValue: 'Subjective' })}</h4>
                  </div>
                  <p className="soap-read-text">
                    {soapData.ownerComplaint || soapData.subjective || (
                      <span className="soap-read-empty">
                        {t('receptionOutpatients.details.soapNotes.sections.subjective.notRecorded', { defaultValue: 'Not recorded' })}
                      </span>
                    )}
                  </p>
                </div>
                <div className="soap-read-card">
                  <div className="soap-read-head">
                    <span className="soap-read-badge objective">O</span>
                    <h4>{t('receptionOutpatients.details.soapNotes.sections.objective.title', { defaultValue: 'Objective' })}</h4>
                  </div>
                  {buildVitalsChips().length > 0 && (
                    <div className="soap-read-chips">
                      {buildVitalsChips().map((chip, idx) => (
                        <span key={idx} className="soap-read-chip">{chip}</span>
                      ))}
                    </div>
                  )}
                  <p className="soap-read-text">
                    {soapData.objective || (
                      <span className="soap-read-empty">
                        {t('receptionOutpatients.details.soapNotes.sections.subjective.notRecorded', { defaultValue: 'Not recorded' })}
                      </span>
                    )}
                  </p>
                </div>
                <div className="soap-read-card">
                  <div className="soap-read-head">
                    <span className="soap-read-badge assessment">A</span>
                    <h4>{t('receptionOutpatients.details.soapNotes.sections.assessment.title', { defaultValue: 'Assessment' })}</h4>
                  </div>
                  <p className="soap-read-text">
                    {soapData.assessment || displayFinalDiagnosis || (
                      <span className="soap-read-empty">
                        {t('receptionOutpatients.details.soapNotes.sections.subjective.notRecorded', { defaultValue: 'Not recorded' })}
                      </span>
                    )}
                  </p>
                </div>
                <div className="soap-read-card soap-read-card-full">
                  <div className="soap-read-head">
                    <span className="soap-read-badge plan">P</span>
                    <h4>{t('receptionOutpatients.details.soapNotes.sections.plan.title', { defaultValue: 'Plan' })}</h4>
                  </div>
                  <p className="soap-read-text">
                    {soapData.plan || (
                      <span className="soap-read-empty">
                        {t('receptionOutpatients.details.soapNotes.sections.subjective.notRecorded', { defaultValue: 'Not recorded' })}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )
        )}
      </div >
    </div >
  );
};

export default SOAPDetailsContent;

